import React from 'react'
import * as yup from 'yup'
import { Box, Grid, MenuItem, Stack, Typography } from '@mui/material'
import FlatTextField from '@components/FlatTextField'
import { ChevronLeft } from '@mui/icons-material'
import FlatPhoneNumber from '@components/FlatPhoneNumber'
import { useFormik } from 'formik'
import { useTranslation } from 'next-i18next'
import { useCountries, useCreateSchoolInfo, useGetCountryCode } from '@api/common'
import { toast } from 'react-toastify'
import FlatButton from '@components/FlatButton'
import ResponsiveButton from '@components/ResponsiveButton'

const validationSchema = yup.object().shape({
  first_name: yup.string().required('Field is required'),
  last_name: yup.string().required('Field is required'),
  city: yup.string().required('Field is required'),
  country: yup.string().required('Field is required'),
  phone: yup.string().required('Field is required'),
})

function InfoStep2({ data, setData, handleBack, handleNext }) {
  const { t } = useTranslation(['common', 'register'])

  const countryCodeQuery = useGetCountryCode()
  const defaultCountry = countryCodeQuery?.data?.toLowerCase() ?? 'sa'

  const createSchoolInfoMutation = useCreateSchoolInfo(data.domain, data.token)
  const formik = useFormik({
    initialValues: {
      first_name: data?.first_name ?? '',
      last_name: data?.last_name ?? '',
      // address: data?.address ?? '',
      city: data?.city ?? '',
      country: data?.country ?? defaultCountry,
      phone: data?.phone ?? '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      setData((prevData) => ({ ...prevData, ...values }))
      createSchoolInfoMutation
        .mutateAsync({ ...data, ...values })
        .then(() => handleNext())
        .catch(() => toast.error('failed to create data'))
        .finally(() => actions.setSubmitting(false))
      actions.setSubmitting(false)
    },
  })

  const countriesQuery = useCountries(data.domain, data.token)

  return (
    <Grid
      container
      spacing={{ xs: 2, md: 5 }}
      onSubmit={formik.handleSubmit}
      component="form"
    >
      <Grid
        item
        md={6}
        xs={12}
      >
        <FlatTextField
          fullWidth
          label={t('register:form.First_name')}
          backgroundColor="neutral.main"
          size="large"
          name="first_name"
          value={formik.values.first_name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.first_name && Boolean(formik.errors.first_name)}
          helperText={formik.touched.first_name && formik.errors.first_name}
          inputProps={{ 'aria-label': t('register:form.First_name') }}
        />
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <FlatTextField
          fullWidth
          label={t('register:form.Last_name')}
          backgroundColor="neutral.main"
          size="large"
          name="last_name"
          value={formik.values.last_name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.last_name && Boolean(formik.errors.last_name)}
          helperText={formik.touched.last_name && formik.errors.last_name}
          inputProps={{ 'aria-label': t('register:form.Last_name') }}
        />
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <FlatTextField
          fullWidth
          label={t('register:form.Country')}
          backgroundColor="neutral.main"
          size="large"
          name="country"
          value={formik.values.country}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.country && Boolean(formik.errors.country)}
          helperText={formik.touched.country && formik.errors.country}
          inputProps={{ 'aria-label': t('register:form.Country') }}
          select
        >
          {countriesQuery.data?.map((country) => (
            <MenuItem
              key={country.name}
              value={country.name}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Box>
                  <img
                    loading="lazy"
                    width="20"
                    src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                    alt=""
                  />
                </Box>
                <Typography>{country.name}</Typography>
              </Stack>
            </MenuItem>
          ))}
        </FlatTextField>
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <FlatTextField
          fullWidth
          label={t('register:form.City')}
          backgroundColor="neutral.main"
          size="large"
          name="city"
          value={formik.values.city}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.city && Boolean(formik.errors.city)}
          helperText={formik.touched.city && formik.errors.city}
          inputProps={{ 'aria-label': t('register:form.City') }}
        />
      </Grid>
      {/* <Grid
        item
        xs={12}
      >
        <FlatTextField
          fullWidth
          label={t('register:form.Address')}
          backgroundColor="neutral.main"
          size="large"
          name="address"
          value={formik.values.address}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.address && Boolean(formik.errors.address)}
          helperText={formik.touched.address && formik.errors.address}
          inputProps={{ 'aria-label': t('register:form.Address') }}
        />
      </Grid> */}
      <Grid
        item
        xs={12}
      >
        <FlatPhoneNumber
          defaultCountry={defaultCountry}
          disableAreaCodes
          fullWidth
          label={t('register:form.Phone_number')}
          variant="outlined"
          backgroundColor="neutral.main"
          name="phone"
          value={formik.values.phone}
          onChange={(value) => formik.setFieldValue('phone', value)}
          onBlur={formik.handleBlur}
          error={formik.touched.phone && Boolean(formik.errors.phone)}
          helperText={formik.touched.phone && formik.errors.phone}
          inputProps={{ 'aria-label': t('register:form.Phone_number') }}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          sx={{ mt: 3 }}
        >
          <ResponsiveButton
            color="neutral"
            sx={{ color: 'neutral.dark' }}
            onClick={handleBack}
            startIcon={<ChevronLeft />}
            aria-label={t('common:back')}
          >
            {t('common:back')}
          </ResponsiveButton>
          <FlatButton
            id="info-step3-button"
            variant="contained"
            type="submit"
            onClick={formik.handleSubmit}
            disableElevation
            aria-label={t('common:submit')}
            isLoading={formik.isSubmitting}
          >
            {t('common:submit')}
          </FlatButton>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default InfoStep2
